export type MobileEventBridge = Pick<
	RallySSO,
	| "getAccessToken"
	| "getApp"
	| "getUser"
	| "showAlert"
	| "launchExternal"
	| "close"
	| "openLeaderboard"
>;

declare global {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	interface Window {
		mlbNative: MobileEventBridge;
	}
	const mlbNative: MobileEventBridge;
}

export default class RallySSO {
	public static nativeBridge: MobileEventBridge | null;

	constructor() {
		RallySSO.nativeBridge = null;
	}

	public static get BUILD_IS_NATIVE(): boolean {
		if (window.location.href.includes("loc")) {
			return false;
		}
		const searchParams = new URLSearchParams(window.location.search);

		return Boolean(searchParams.get("isNative"));
	}

	static IS_NATIVE = RallySSO.BUILD_IS_NATIVE;

	public async init() {
		try {
			await this.setNativeBridge();
		} catch (error) {
			console.log("Error: ", error);
			if (typeof error === "string") {
				throw new Error(`Initialization Error: ${error}`);
			}

			throw new Error(`Initialization Error: ${(error as Error)?.message}`);
		}
	}

	public async getAccessToken(): Promise<string | null> {
		if (RallySSO.IS_NATIVE) {
			return RallySSO.nativeBridge?.getAccessToken() || null;
		}

		return Promise.resolve(null);
	}

	public async getApp(): Promise<string | null> {
		if (RallySSO.IS_NATIVE) {
			return RallySSO.nativeBridge?.getApp() || null;
		}

		return Promise.resolve(null);
	}

	public async getUser(): Promise<string | null> {
		if (RallySSO.IS_NATIVE) {
			return RallySSO.nativeBridge?.getUser() || null;
		}

		return Promise.resolve(null);
	}

	public openLeaderboard(): void {
		if (RallySSO.IS_NATIVE) {
			RallySSO.nativeBridge?.openLeaderboard();
		}
	}

	public showAlert(message: string) {
		if (RallySSO.IS_NATIVE) {
			RallySSO.nativeBridge?.showAlert(message);
		}
	}

	public launchExternal(url: string) {
		if (RallySSO.IS_NATIVE) {
			RallySSO.nativeBridge?.launchExternal(url);
		} else {
			window.location.href = url;
		}
	}

	public close() {
		if (RallySSO.IS_NATIVE) {
			RallySSO.nativeBridge?.close();
		}
	}

	private setNativeBridge() {
		if (RallySSO.IS_NATIVE) {
			return new Promise((resolve, reject) => {
				let timer: NodeJS.Timeout;
				let timesCalled = 0;
				const checkState = (): void => {
					if (typeof mlbNative !== "undefined" && !RallySSO.nativeBridge) {
						RallySSO.nativeBridge = mlbNative;

						clearTimeout(timer);
						resolve(RallySSO.nativeBridge);
					} else {
						timesCalled = timesCalled + 1;

						if (timesCalled === 50) {
							clearTimeout(timer);
							reject("Native bridge was not set");
							return;
						}

						timer = setTimeout(checkState, 200);
					}
				};

				checkState();
			});
		}

		return Promise.resolve();
	}
}
